import React, { useState, useEffect } from 'react';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/ui/use-toast';
import { Loader2, ExternalLink, FileText } from 'lucide-react';
import { Link } from 'react-router-dom';
import { SettingsCard } from '@/components/settings/ui/SettingsCard';

interface BusinessPolicyEditorProps {
  businessId: string;
}

const BusinessPolicyEditor: React.FC<BusinessPolicyEditorProps> = ({ businessId }) => {
  const [customPolicy, setCustomPolicy] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasPolicy, setHasPolicy] = useState(false);
  const policyCardRef = React.useRef<HTMLDivElement>(null);

  useEffect(() => {
    fetchPolicy();
  }, [businessId]);

  // Effect to handle privacy policy highlighting from ReviewFormBuilder
  useEffect(() => {
    const shouldHighlight = sessionStorage.getItem('highlightPrivacyPolicy');

    // Only proceed if we should highlight AND loading is complete
    if (shouldHighlight === 'true' && !loading && policyCardRef.current) {
      // Clear the flag
      sessionStorage.removeItem('highlightPrivacyPolicy');

      // Small delay to ensure DOM is fully painted
      requestAnimationFrame(() => {
        setTimeout(() => {
          if (policyCardRef.current) {
            // Scroll to the privacy policy card
            policyCardRef.current.scrollIntoView({
              behavior: 'smooth',
              block: 'center'
            });

            // Add highlight classes
            policyCardRef.current.classList.add(
              'ring-2',
              'ring-blue-500',
              'ring-offset-2',
              'transition-all',
              'duration-300'
            );

            // Remove highlight after 3 seconds
            setTimeout(() => {
              if (policyCardRef.current) {
                policyCardRef.current.classList.remove(
                  'ring-2',
                  'ring-blue-500',
                  'ring-offset-2',
                  'transition-all',
                  'duration-300'
                );
              }
            }, 3000);
          }
        }, 100);
      });
    }
  }, [loading]); // Trigger when loading changes

  const fetchPolicy = async () => {
    try {
      const { data, error } = await supabase
        .from('business_policies')
        .select('custom_policy')
        .eq('business_id', businessId)
        .maybeSingle();

      if (error && error.code !== 'PGRST116') {
        throw error;
      }

      if (data) {
        setCustomPolicy(data.custom_policy || '');
        setHasPolicy(true);
      } else {
        setHasPolicy(false);
      }
    } catch (error) {
      console.error('Error fetching business policy:', error);
      toast.error('Failed to load policy - Could not load your custom privacy policy');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!customPolicy.trim()) {
      toast.error('Policy cannot be empty - Please enter your custom privacy policy before saving');
      return;
    }

    setSaving(true);
    try {
      if (hasPolicy) {
        // Update existing policy
        const { error } = await supabase
          .from('business_policies')
          .update({ custom_policy: customPolicy })
          .eq('business_id', businessId);

        if (error) throw error;
      } else {
        // Insert new policy
        const { error } = await supabase
          .from('business_policies')
          .insert({ business_id: businessId, custom_policy: customPolicy });

        if (error) throw error;
        setHasPolicy(true);
      }

      toast.success('Policy saved successfully - Your custom privacy policy has been updated');
    } catch (error) {
      console.error('Error saving business policy:', error);
      toast.error('Failed to save policy - Could not save your custom privacy policy');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <SettingsCard
        icon={FileText}
        title="Privacy Policy"
        description="Add your custom privacy policy for forms"
      >
        <div className="flex items-center justify-center py-8">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </SettingsCard>
    );
  }

  return (
    <SettingsCard
      ref={policyCardRef}
      icon={FileText}
      title="Privacy Policy"
      description="Add your custom privacy policy that will be displayed alongside TagInn's privacy policy on your forms. This policy will be shown on review forms, loyalty program forms, and other public-facing forms."
    >
      <div className="space-y-4">
        <div className="space-y-2">
          <label htmlFor="custom-policy" className="text-sm font-medium">
            Your Custom Privacy Policy
          </label>
          <Textarea
            id="custom-policy"
            placeholder="Enter your custom privacy policy here. This will be appended to the standard TagInn privacy policy..."
            value={customPolicy}
            onChange={(e) => setCustomPolicy(e.target.value)}
            rows={12}
            className="font-mono text-sm"
          />
          <p className="text-xs text-muted-foreground">
            You can use plain text. Line breaks will be preserved.
          </p>
        </div>

        <div className="flex items-center gap-4">
          <Button onClick={handleSave} disabled={saving || !customPolicy.trim()}>
            {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Save Policy
          </Button>

          {hasPolicy && (
            <Link
              to={`/privacy/${businessId}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center text-sm text-primary hover:underline"
            >
              Preview your privacy policy
              <ExternalLink className="ml-1 h-4 w-4" />
            </Link>
          )}
        </div>

        {!hasPolicy && customPolicy.trim() && (
          <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
            <p className="text-sm text-yellow-800 dark:text-yellow-200">
              You must save your policy before you can create review forms or loyalty programs.
            </p>
          </div>
        )}
      </div>
    </SettingsCard>
  );
};

export default BusinessPolicyEditor;
